// Buscador de pacientes (Rediseño mayor 2026-09-02, Fase 1 — "destinos
// reales del nav") — base de la pestaña "Pacientes" y de "Nueva consulta"/
// "Ver historia clínica" de Accesos Rápidos (Dashboard). Búsqueda
// client-side sobre GET /api/hce/pacientes (TODOS los pacientes del
// tenant, no acotado a especialidad vinculada — pedido explícito del
// usuario, a diferencia del resto del Dashboard).
//
// El query param `intent` decide qué hacer al elegir un paciente:
// "consulta" → mismo camino EXACTO que "Iniciar nueva consulta" de
// PatientChartPage.tsx (asegurarEncounterDeHoy + /notas, desde
// 2026-09-02) — con intent=consulta también se puede dar de alta un
// paciente NUEVO (NuevoPacienteForm.tsx, Fase 6) en vez de buscar uno
// existente;
// "historia" → directo a su pestaña Historia; "subir" → directo a
// Documentos con el diálogo de subida abierto; sin intent (entrada normal
// desde el nav) → a su ficha (resumen).
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TopNav } from '@/dashboard/TopNav';
import { listarPacientesHce, type PacienteHce } from '@/lib/hceVault';
import { puedeAccionProfesional, useRolDelPracticante } from '@/lib/useRolDelPracticante';
import { asegurarEncounterDeHoy } from '@/paciente/asegurarEncounterDeHoy';
import { NuevoPacienteForm } from './NuevoPacienteForm';

type Intent = 'consulta' | 'historia' | 'subir' | null;

export function PacientesPage(): JSX.Element {
  // BA-C-11: el alta de pacientes la rechaza el servidor (403) a quien no es profesional clínico; se oculta el botón.
  const puedeAlta = puedeAccionProfesional(useRolDelPracticante(), 'alta-paciente');
  const { t } = useTranslation();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intent = (searchParams.get('intent') as Intent) ?? null;

  const [pacientes, setPacientes] = useState<PacienteHce[] | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [procesandoId, setProcesandoId] = useState<number | null>(null);
  const [dandoDeAlta, setDandoDeAlta] = useState(false);
  const [entrandoAConsulta, setEntrandoAConsulta] = useState(false);

  useEffect(() => {
    listarPacientesHce()
      .then(setPacientes)
      .catch(() => setPacientes([]));
  }, []);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return pacientes ?? [];
    return (pacientes ?? []).filter(
      (p) => p.nombre.toLowerCase().includes(q) || p.idNumber.toLowerCase().includes(q) || p.phone.toLowerCase().includes(q),
    );
  }, [pacientes, busqueda]);

  async function entrarAConsulta(medplumPatientId: string): Promise<void> {
    setEntrandoAConsulta(true);
    try {
      // Ídem PatientChartPage.tsx (entrarAConsulta ahí, mismo nombre a
      // propósito) — aterriza en Notas, no en el catálogo completo de
      // Formularios (pedido explícito del usuario 2026-09-02).
      await asegurarEncounterDeHoy(medplum, medplumPatientId);
      void navigate(`/${medplumPatientId}/notas`);
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
      setEntrandoAConsulta(false);
    }
  }

  async function elegirPaciente(p: PacienteHce): Promise<void> {
    if (!p.medplumPatientId) return;
    if (intent === 'consulta') {
      setProcesandoId(p.patientId);
      await entrarAConsulta(p.medplumPatientId);
      setProcesandoId(null);
      return;
    }
    if (intent === 'historia') {
      void navigate(`/${p.medplumPatientId}/historia`);
      return;
    }
    if (intent === 'subir') {
      void navigate(`/${p.medplumPatientId}/documentos?subir=1`);
      return;
    }
    void navigate(`/${p.medplumPatientId}`);
  }

  if (dandoDeAlta) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 py-4">
        <TopNav activo="pacientes" />
        <h1 className="text-lg font-medium text-hce-ink">{t('nuevoPaciente.titulo')}</h1>
        {entrandoAConsulta ? (
          <p className="text-sm text-hce-muted">{t('nuevoPaciente.entrandoAConsulta')}</p>
        ) : (
          <NuevoPacienteForm onCreado={(r) => void entrarAConsulta(r.medplumPatientId)} onCancelar={() => setDandoDeAlta(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 py-4">
      <TopNav activo="pacientes" />
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-medium text-hce-ink">{t('pacientes.titulo')}</h1>
        {intent === 'consulta' && puedeAlta && (
          <Button size="sm" variant="outline" onClick={() => setDandoDeAlta(true)}>
            {t('pacientes.pacienteNuevo')}
          </Button>
        )}
      </div>
      {intent && <p className="text-sm text-hce-muted">{t(`pacientes.intent.${intent}`)}</p>}

      <Input
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder={t('pacientes.buscarPlaceholder')}
      />

      <div className="rounded-lg border border-border bg-card">
        {pacientes === null ? (
          <p className="p-4 text-sm text-hce-muted">{t('dashboard.cargando')}</p>
        ) : filtrados.length === 0 ? (
          <p className="p-4 text-sm text-hce-muted">{t('pacientes.sinResultados')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('pacientes.nombre')}</TableHead>
                <TableHead>{t('pacientes.cedula')}</TableHead>
                <TableHead>{t('pacientes.telefono')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtrados.map((p) => (
                <TableRow
                  key={p.patientId}
                  className={p.medplumPatientId ? 'cursor-pointer' : 'opacity-60'}
                  onClick={() => void elegirPaciente(p)}
                >
                  <TableCell className="text-hce-ink">{p.nombre}</TableCell>
                  <TableCell className="text-hce-muted">{p.idNumber}</TableCell>
                  <TableCell className="text-hce-muted">
                    {p.phone}
                    {!p.medplumPatientId && <span className="ml-2 text-xs">{t('dashboard.pacienteSinSincronizar')}</span>}
                    {procesandoId === p.patientId && <span className="ml-2 text-xs">{t('dashboard.cargando')}</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
